import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GlossaryTerm } from "@/components/GlossaryTerm";
import { Activity, TrendingUp, TrendingDown, Minus, Gauge } from "lucide-react";

// Maps each posture component to the closest glossary term for inline definitions.
const DRIVER_TERM = {
  residual_pressure: "Residual score",
  signal_pressure: "Signal",
  supplier_pressure: "Supplier tier",
  premortem_pressure: "Pre-mortem alert",
};

const BAND_COLOR = {
  Stable: { stroke: "#059669", text: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  Elevated: { stroke: "#d97706", text: "text-amber-600", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  High: { stroke: "#ea580c", text: "text-orange-600", chip: "bg-orange-50 text-orange-700 border-orange-200" },
  Critical: { stroke: "#dc2626", text: "text-red-600", chip: "bg-red-50 text-red-700 border-red-200" },
};

const R = 80;
const C = 2 * Math.PI * R;
const SWEEP = 0.75; // 270° gauge
const TRACK = C * SWEEP;

function Dial({ index, band }) {
  const pct = Math.max(0, Math.min(100, index)) / 100;
  const color = BAND_COLOR[band] || BAND_COLOR.Elevated;
  return (
    <svg viewBox="0 0 200 200" className="h-44 w-44" data-testid="posture-dial">
      <g transform="rotate(135 100 100)">
        <circle
          cx="100" cy="100" r={R} fill="none" stroke="#e5e7eb" strokeWidth="14"
          strokeDasharray={`${TRACK} ${C}`} strokeLinecap="round"
        />
        <circle
          cx="100" cy="100" r={R} fill="none" stroke={color.stroke} strokeWidth="14"
          strokeDasharray={`${TRACK * pct} ${C}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.9s cubic-bezier(0.4,0,0.2,1)" }}
        />
      </g>
      <text x="100" y="96" textAnchor="middle" className="fill-ek-ink font-display" style={{ fontSize: 44, fontWeight: 800 }}>
        {index}
      </text>
      <text x="100" y="120" textAnchor="middle" className="fill-current" style={{ fontSize: 12, letterSpacing: 1 }}>
        / 100 exposure
      </text>
    </svg>
  );
}

function Sparkline({ trend, stroke }) {
  if (!trend || trend.length < 2) return null;
  const w = 260, h = 56, pad = 6;
  const xs = trend.map((_, i) => pad + (i * (w - 2 * pad)) / (trend.length - 1));
  const min = Math.min(...trend.map((t) => t.index));
  const max = Math.max(...trend.map((t) => t.index));
  const span = Math.max(1, max - min);
  const ys = trend.map((t) => h - pad - ((t.index - min) / span) * (h - 2 * pad));
  const pts = xs.map((x, i) => `${x},${ys[i]}`).join(" ");
  const area = `${xs[0]},${h} ` + pts + ` ${xs[xs.length - 1]},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-14 w-full" data-testid="posture-sparkline">
      <polygon points={area} fill={stroke} opacity="0.08" />
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r="3.5" fill={stroke} />
    </svg>
  );
}

function ThemeBar({ t }) {
  const lvl = t.exposure;
  const stroke = lvl >= 70 ? "bg-red-500" : lvl >= 50 ? "bg-orange-500" : lvl >= 30 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="flex items-center gap-3" data-testid={`posture-theme-${t.theme_id}`}>
      <span className="w-40 shrink-0 truncate text-xs text-ek-ink/60">{t.name}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
        <div className={`h-full rounded-full ${stroke}`} style={{ width: `${lvl}%`, transition: "width 0.9s ease" }} />
      </div>
      <span className="w-7 shrink-0 text-right text-xs font-semibold tabular-nums text-ek-ink/70">{lvl}</span>
    </div>
  );
}

export const RiskPosture = () => {
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get("/dashboard/posture").then(({ data }) => setP(data)).catch(() => {});
  }, []);

  if (!p) return null;
  const color = BAND_COLOR[p.band] || BAND_COLOR.Elevated;
  const delta = p.delta || 0;
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  // Higher exposure is worse: a rise (delta>0) is bad (red), a fall is good (green).
  const deltaTone = delta > 0 ? "text-red-700" : delta < 0 ? "text-emerald-700" : "text-ek-ink/65";

  return (
    <section className="mt-10" data-testid="risk-posture-section">
      <h2 className="ek-eyebrow flex items-center gap-1">
        <Gauge size={12} /> Risk posture
        {p.scoped && (
          <span className="ml-2 rounded-full bg-ek-purple/10 px-2 py-0.5 text-[10px] font-semibold text-ek-purple" data-testid="posture-scoped-badge">
            Scoped to your domains
          </span>
        )}
      </h2>
      <p className="mt-1 text-sm text-ek-ink/65">
        A living composite of residual risk, critical signals, pre-mortem alerts and supplier exposure — updated every visit.
      </p>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {/* Hero dial */}
        <div className="flex flex-col items-center justify-center rounded-md border border-black/5 bg-white p-6" data-testid="posture-hero">
          <div className={color.text}>
            <Dial index={p.index} band={p.band} />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className={`rounded-full border px-3 py-0.5 text-xs font-bold uppercase tracking-wide ${color.chip}`} data-testid="posture-band">
              {p.band}
            </span>
            <span className={`flex items-center gap-1 text-xs font-semibold ${deltaTone}`} data-testid="posture-delta">
              <DeltaIcon size={13} />
              {delta > 0 ? "+" : ""}{delta} vs prev
            </span>
          </div>
        </div>

        {/* Trend + drivers */}
        <div className="rounded-md border border-black/5 bg-white p-6">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-ek-ink/65">14-day trend</p>
            <Activity size={14} className="text-ek-ink/30" />
          </div>
          <Sparkline trend={p.trend} stroke={color.stroke} />
          <div className="mt-4 space-y-2.5">
            {(p.component_meta || []).map((c) => {
              const v = p.components?.[c.key] ?? 0;
              return (
                <div key={c.key} className="flex items-center gap-3" data-testid={`posture-driver-${c.key}`}>
                  <span className="w-44 shrink-0 text-xs text-ek-ink/60">
                    <GlossaryTerm k={DRIVER_TERM[c.key]} label={c.label} testid={`posture-driver-term-${c.key}`} />
                  </span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
                    <div className="h-full rounded-full bg-ek-purple" style={{ width: `${v}%` }} />
                  </div>
                  <span className="w-7 shrink-0 text-right text-xs font-semibold tabular-nums text-ek-ink/70">{v}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Per-theme exposure */}
        <div className="rounded-md border border-black/5 bg-white p-6">
          <p className="text-xs font-medium uppercase tracking-wide text-ek-ink/65">Exposure by theme</p>
          <div className="mt-4 space-y-2.5">
            {(p.themes || []).map((t) => (
              <ThemeBar key={t.theme_id} t={t} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
